/** Returns a short relative-time string like "just now", "5m ago", "3h ago",
 * "2d ago", "3 weeks ago", "6 months ago". */
export function relativeTime(iso: string | number | Date): string {
  const then = typeof iso === "string" || typeof iso === "number"
    ? new Date(iso).getTime()
    : iso.getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));

  if (diffSec < 10) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "yesterday";
  if (diffDay < 7) return `${diffDay}d ago`;
  if (diffDay < 30) return `${Math.floor(diffDay / 7)}w ago`;
  if (diffDay < 365) return `${Math.floor(diffDay / 30)}mo ago`;
  return `${Math.floor(diffDay / 365)}y ago`;
}

/** Formats a bytes count into "X.Y MB" or "X KB". */
export function formatBytes(bytes: number | undefined | null): string {
  if (!bytes || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/** Placeholder regex shared between teleprompter + new-link preview. */
export const PLACEHOLDER_REGEX = /(\.\.\.\([^)]*\)\.\.\.)/g;
export const PLACEHOLDER_INNER = /^\.\.\.\(([^)]*)\)\.\.\.$/;

// Combined tokenizer: ...(lemonade)..., [orange]. Used to split a line into
// renderable parts while preserving order.
export const SCRIPT_TOKEN_REGEX = /(\.\.\.\([^)]*\)\.\.\.|\[[^\]]*\])/g;
export const BRACKET_INNER = /^\[([^\]]*)\]$/;

// Segment marker: a line starting with "(N)", "(N.M)", or "()" (with any
// surrounding whitespace). An optional "*" right before the parenthesis opts
// the segment into karaoke mode (progressive per-word underline). The first
// capture group is the karaoke flag ("*" or empty); the second is the numeric
// content — empty string means "auto-distribute".
const SEGMENT_MARKER = /^\s*(\*?)\s*\((\d*(?:\.\d+)?)\)\s*/;

export interface Segment {
  /** Segment text (may contain newlines — line breaks within a segment). */
  raw: string;
  /** Explicit duration in seconds from "(N)" marker, undefined when "()" or unset. */
  duration?: number;
  /** When true, render with a karaoke-style progressive underline. Opted in
   *  by prefixing the segment marker with "*", e.g. "*(5)" or "*()". */
  karaoke?: boolean;
}

export interface ParsedScript {
  /** Lines shown before recording starts (before the "---" separator). */
  intro: string[];
  /** Recording-time segments delimited by "(N)" / "()" markers. */
  segments: Segment[];
}

/** Parse a raw teleprompter script into intro + segments.
 *  - "---" on its own line separates intro from recording body.
 *  - In the body, "(N)" or "()" at the start of a line opens a new segment.
 *  - Lines without a marker append to the current segment (preserved newline).
 *  - Legacy fallback: if the body has NO markers at all, each non-empty line
 *    becomes its own auto-duration segment (compat with pre-v2 scripts). */
export function parseScript(text: string): ParsedScript {
  const lines = text.split(/\r?\n/);
  const sepIdx = lines.findIndex((l) => l.trim() === "---");
  const introRaw = sepIdx >= 0 ? lines.slice(0, sepIdx) : [];
  const bodyRaw = sepIdx >= 0 ? lines.slice(sepIdx + 1) : lines;

  const intro = introRaw.map((l) => l.trim()).filter(Boolean);

  const hasAnyMarker = bodyRaw.some((l) => SEGMENT_MARKER.test(l));
  if (!hasAnyMarker) {
    const segs: Segment[] = bodyRaw
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line) => ({ raw: line }));
    return { intro, segments: segs };
  }

  const segments: Segment[] = [];
  let current: { lines: string[]; duration?: number; karaoke?: boolean } | null =
    null;
  const flush = () => {
    if (!current) return;
    const raw = current.lines.join("\n").replace(/^\n+|\n+$/g, "");
    if (raw) {
      const seg: Segment = { raw, duration: current.duration };
      if (current.karaoke) seg.karaoke = true;
      segments.push(seg);
    }
    current = null;
  };

  for (const line of bodyRaw) {
    const m = line.match(SEGMENT_MARKER);
    if (m) {
      flush();
      const rest = line.slice(m[0].length);
      const dur = m[2] === "" ? undefined : parseFloat(m[2]);
      const karaoke = m[1] === "*";
      current = { lines: rest ? [rest] : [], duration: dur, karaoke };
    } else if (current) {
      current.lines.push(line);
    } else if (line.trim()) {
      // Lines before the first marker → initial auto-duration segment.
      current = { lines: [line], duration: undefined };
    }
  }
  flush();

  return { intro, segments };
}

/** Strip markup and count whitespace-separated tokens. Used as a weight when
 *  distributing time across auto-duration segments. */
function wordCount(s: string): number {
  const cleaned = s
    .replace(/\.\.\.\(([^)]*)\)\.\.\./g, "$1")
    .replace(/\[([^\]]*)\]/g, "$1");
  return cleaned.split(/\s+/).filter(Boolean).length;
}

/** Compute per-segment timing in seconds.
 *  - Segments with explicit "(N)" durations keep N (scaled down if their sum
 *    overflows maxSec).
 *  - Auto segments ("()" or no marker) share the remaining time proportionally
 *    to their word count, so a long sentence gets more screen time than a
 *    short one. */
export function distributeTiming(
  segments: Segment[],
  maxSec: number,
): { start: number; dur: number }[] {
  if (segments.length === 0) return [];
  const explicitSum = segments.reduce((s, x) => s + (x.duration ?? 0), 0);
  const autoSegs = segments.filter((x) => x.duration === undefined);
  const autoCount = autoSegs.length;

  let scale = 1;
  let autoBudget = 0;
  if (autoCount === 0) {
    if (explicitSum > 0) scale = maxSec / explicitSum;
  } else if (explicitSum >= maxSec) {
    // Explicit alone overflows: shrink them to 90% of total, give 10% to auto.
    scale = (maxSec * 0.9) / explicitSum;
    autoBudget = maxSec * 0.1;
  } else {
    autoBudget = maxSec - explicitSum;
  }

  const autoWordSum =
    autoSegs.reduce((acc, seg) => acc + Math.max(1, wordCount(seg.raw)), 0) || 1;

  let t = 0;
  return segments.map((seg) => {
    let dur: number;
    if (seg.duration !== undefined) {
      dur = seg.duration * scale;
    } else {
      const w = Math.max(1, wordCount(seg.raw));
      dur = (w / autoWordSum) * autoBudget;
    }
    const out = { start: t, dur };
    t += dur;
    return out;
  });
}

/** Find which segment index is active at a given elapsed-second mark. */
export function currentSegmentIdx(
  elapsedSec: number,
  timing: { start: number; dur: number }[],
): number {
  if (timing.length === 0) return -1;
  for (let i = 0; i < timing.length; i++) {
    if (elapsedSec >= timing[i].start && elapsedSec < timing[i].start + timing[i].dur) {
      return i;
    }
  }
  return timing.length - 1;
}

// Emoticon → emoji map, longest patterns first so ":'(" wins over ":(".
const EMOJI_PAIRS: [RegExp, string][] = [
  [/:'\(/g, "😢"],
  [/:D(?=\W|$)/g, "😀"],
  [/XD(?=\W|$)/g, "😆"],
  [/:[pP](?=\W|$)/g, "😛"],
  [/:[oO](?=\W|$)/g, "😮"],
  [/;\)/g, "😉"],
  [/:\)/g, "🙂"],
  [/:\(/g, "🙁"],
  [/<3/g, "❤️"],
];

/** Replace text emoticons with unicode emoji. Safe inside placeholders. */
export function emojify(text: string): string {
  return EMOJI_PAIRS.reduce((s, [re, em]) => s.replace(re, em), text);
}
